import { useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import api from 'services/api'
import useToast from 'hooks/useToast'
import useModalsActions from 'store/actions/modals'

const SUCCESS_STATUSES = new Set(['success_unviewed', 'success_viewed'])

// Cache resolved video URLs per source_id so reclicks/rerenders skip the fetch.
const urlCache = new Map()

const AnimatedBadge = ({ reclip, size = 20, sx }) => {
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const { openModal } = useModalsActions()

  const status = reclip?.animated_video?.status
  const thumbUrl = reclip?.animated_video?.thumb_url
  const animationType = reclip?.animated_video?.animation_type
  const sourceId = reclip?.source_id
  const canClick = !!sourceId && SUCCESS_STATUSES.has(status)
  const hasOutline =
    animationType?.startsWith('ai') || animationType === 'emoticon'

  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation()
      if (!canClick || isLoading) return

      const cached = urlCache.get(sourceId)
      if (cached) {
        window.open(cached, '_blank', 'noopener,noreferrer')
        return
      }

      setIsLoading(true)
      try {
        const { video_url } = await api.animatedVideos.getAnimatedVideoByReclip(
          sourceId
        )
        if (video_url) {
          urlCache.set(sourceId, video_url)
          window.open(video_url, '_blank', 'noopener,noreferrer')
        } else {
          toast({ severity: 'error', message: 'No video URL found' })
        }
      } catch (err) {
        toast({ severity: 'error', message: err.message })
      } finally {
        setIsLoading(false)
      }
    },
    [canClick, isLoading, sourceId, toast]
  )

  const handleInfoClick = useCallback(
    (e) => {
      e.stopPropagation()
      openModal('animationOutline', { sourceId })
    },
    [openModal, sourceId]
  )

  if (!status || !SUCCESS_STATUSES.has(status)) return null

  const baseStyles = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
    cursor: isLoading ? 'wait' : 'pointer',
    opacity: isLoading ? 0.6 : 1,
    transition: 'transform 0.1s ease, opacity 0.1s ease',
    '&:hover': isLoading ? undefined : { transform: 'scale(1.1)' },
  }

  const infoIcon =
    hasOutline && sourceId ? (
      <Box
        onClick={handleInfoClick}
        title="view animation details"
        role="button"
        sx={{
          position: 'absolute',
          top: -4,
          right: -4,
          width: Math.max(size * 0.45, 14),
          height: Math.max(size * 0.45, 14),
          borderRadius: '50%',
          backgroundColor: '#fff',
          color: '#555',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#e0e0e0' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: Math.max(size * 0.35, 10) }} />
      </Box>
    ) : null

  if (thumbUrl) {
    return (
      <Box
        onClick={handleClick}
        title={isLoading ? 'loading animation...' : 'watch animation'}
        role="button"
        sx={{
          ...baseStyles,
          position: 'relative',
          border: '2px solid #DAFC79',
          backgroundColor: '#DAFC79',
          backgroundImage: `url("${thumbUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          ...sx,
        }}
      >
        {infoIcon}
      </Box>
    )
  }

  return (
    <Box
      onClick={handleClick}
      title={isLoading ? 'loading animation...' : 'watch animation'}
      role="button"
      sx={{
        ...baseStyles,
        position: 'relative',
        backgroundColor: '#DAFC79',
        color: '#121212',
        ...sx,
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: size * 0.65 }} />
      {infoIcon}
    </Box>
  )
}

export default AnimatedBadge
